import { FullScreenSpinnerLoader } from "@/components/full-screen-spinner-loader";
import { Head } from "@/components/head";
import { Button } from "@/components/ui/button";
import { usePartnerData } from "@/features/consultation/hooks/use-partner-data";
import { useProviderName } from "@/features/consultation/hooks/use-provider-name";
import { usePartnerPlansData } from "@/features/consultation/hooks/use-partner-plans-data";
import { useHMOStore } from "@/features/hmo-consultation/hooks/use-hmo-store";
import { useClarity } from "@/hooks/use-clarity";
import { API_KEY_LS_KEY } from "@/lib/constants";
import { setPartnerSubdomain, trackMixpanelEvent } from "@/lib/mixpanel";
import {
  setPrimaryColor,
  formulateAndUpdateManifest,
  getProviderFeatures,
} from "@/lib/utils";
import { createFileRoute, Outlet, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

export const Route = createFileRoute("/hmo/$subdomain")({
  component: RouteComponent,
});

// Ensure we can load data for this subdomain / partner before loading any of the other sub-routes
function RouteComponent() {
  useClarity();
  const { subdomain } = Route.useParams();
  const {
    loading: partnerDataLoading,
    error: partnerDataError,
    partnerData,
  } = usePartnerData({ subdomain });
  const { plansData, loading: plansDataLoading } = usePartnerPlansData({
    providerId: partnerData?.providerId,
  });
  const { providerName } = useProviderName({
    providerId: partnerData?.providerId,
  });

  const loading = partnerDataLoading || plansDataLoading;

  const searchParams = useSearch({ from: Route.id });
  // @ts-expect-error property via does not exist. need to add 'via' to search params
  const via = searchParams.via;

  // Fires once per subdomain regardless of which child route (landing page
  // or a deep link straight to create-consultation) the weblink points at —
  // this marks "user hit the link and reached the app", not "saw the
  // landing page" specifically.
  const trackedLandingRef = useRef(false);

  useEffect(() => {
    if (!subdomain || trackedLandingRef.current) return;
    trackedLandingRef.current = true;

    // Best-effort only — must never be able to crash the app.
    try {
      setPartnerSubdomain(subdomain);
      trackMixpanelEvent("hmo_landing_viewed", {
        via: via ?? undefined,
        source: "weblink",
        partner: subdomain.charAt(0).toUpperCase() + subdomain.slice(1),
      });
    } catch (error) {
      console.warn("[Mixpanel] hmo_landing_viewed tracking failed", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain]);

  const { widgetColor, widgetLogo } = partnerData || {};

  useEffect(() => {
    setPrimaryColor(widgetColor || "");
    formulateAndUpdateManifest(subdomain, widgetLogo || "");
  }, [widgetColor, widgetLogo, subdomain]);

  const { setValues: setStoreValues } = useHMOStore();

  useEffect(() => {
    setStoreValues({
      plans: plansData,
      partnerInfo: {
        ...(partnerData || {}),
      },
      providerName,
      providerFeatures: getProviderFeatures(
        partnerData?.providerId || undefined,
      ),
    });
    sessionStorage.setItem(API_KEY_LS_KEY, partnerData?.apiKey || "");
  }, [partnerData, plansData, providerName, setStoreValues]);

  if (loading) return <FullScreenSpinnerLoader />;

  // show error view if there's an error loading partner data
  if (partnerDataError)
    return <PartnerErrorView errorMessage={partnerDataError.message} />;

  return (
    <>
      <Head partnerName={providerName} />
      <Outlet />
    </>
  );
}

type PartnerErrorView = {
  errorMessage: string;
};

function PartnerErrorView(props: PartnerErrorView) {
  const { errorMessage } = props;

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="rounded-lg bg-white border border-neutral-100 p-6 lg:p-12">
        <div className="text-center flex flex-col justify-center items-center">
          {
            <>
              <p className="text-lg font-medium">Couldn't load data!</p>
              <p className="text-tertiary mt-2 rounded-md p-2 border border-neutral-100 bg-primary-50 text-sm">
                {errorMessage}
              </p>
              <p className="text-tertiary mt-2">Check the link</p>
              <p className="text-tertiary mt-2 text-xs">or</p>
              <Button
                className="mt-4 w-[200px]"
                onClick={() => window.location.reload()}
              >
                Try again
              </Button>
            </>
          }
        </div>
      </div>
    </div>
  );
}
